"use client"

import type { PropsWithChildren, ReactNode } from "react"
import * as React from "react"
import { useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { cn } from "@productlane/lib/src/browser"

import type * as PopoverPrimitive from "@radix-ui/react-popover"
import {
	InternalPopover,
	InternalPopoverAnchor,
	InternalPopoverContent,
	InternalPopoverPortal,
	InternalPopoverTrigger,
} from "./internal/popover"
import { Tooltip } from "./tooltip"

export const POPOVER_AUTOFOCUS_ATTRIBUTE = "data-autofocus"

export interface PopoverProps
	extends PropsWithChildren<unknown>, PopoverPrimitive.PopoverProps {
	content: ReactNode
	className?: string
	style?: Record<string, unknown>
	anchor?: ReactNode
	anchorContainerRef?: React.RefObject<HTMLElement | null>
	align?: PopoverPrimitive.PopoverContentProps["align"]
	side?: PopoverPrimitive.PopoverContentProps["side"]
	sideOffset?: PopoverPrimitive.PopoverContentProps["sideOffset"]
	collisionPadding?: PopoverPrimitive.PopoverContentProps["collisionPadding"]
	onCloseAutoFocus?: PopoverPrimitive.PopoverContentProps["onCloseAutoFocus"]
	onOpenAutoFocus?: PopoverPrimitive.PopoverContentProps["onOpenAutoFocus"]
	container?: PopoverPrimitive.PopoverPortalProps["container"]
	id?: string
	triggerTooltip?: ReactNode
	isPortal?: boolean
}

export const Popover = React.memo(
	React.forwardRef<HTMLDivElement, PopoverProps>(
		(
			{
				children,
				content,
				open,
				defaultOpen,
				className,
				onOpenChange,
				modal = true,
				container,
				anchor,
				anchorContainerRef,
				align = "start",
				side = "bottom",
				sideOffset,
				collisionPadding = 16,
				triggerTooltip,
				onCloseAutoFocus,
				onOpenAutoFocus,
				style,
				isPortal = true,
			},
			ref,
		) => {
			const [isOpen, setIsOpen] = useState<boolean>(!!defaultOpen)

			// This component supports either internal or external state management
			const [internalOpen, internalOnOpenChange] =
				onOpenChange && open !== undefined
					? [open, onOpenChange]
					: [isOpen, (o: boolean) => setIsOpen(o)]

			// Pickers gate their queries on `open`, so their lists empty the moment they
			// close; keep the open content on screen until the exit animation ends.
			const openContentRef = useRef(content)
			if (internalOpen) openContentRef.current = content
			const renderedContent = internalOpen ? content : openContentRef.current

			// Content that focuses itself on mount (a calendar day) loses that focus inside
			// a dialog, whose trap runs before this scope mounts, so focus it here instead.
			const handleOpenAutoFocus = (e: Event) => {
				onOpenAutoFocus?.(e)
				if (e.defaultPrevented || !(e.currentTarget instanceof HTMLElement)) {
					return
				}
				const target = e.currentTarget.querySelector<HTMLElement>(
					`[${POPOVER_AUTOFOCUS_ATTRIBUTE}]`,
				)
				if (target) {
					e.preventDefault()
					target.focus()
				}
			}

			// Track the anchor container element so the portaled anchor re-renders
			// once the ref is populated.
			const [anchorContainerEl, setAnchorContainerEl] =
				useState<HTMLElement | null>(null)
			useLayoutEffect(() => {
				setAnchorContainerEl(anchorContainerRef?.current ?? null)
			}, [anchorContainerRef, internalOpen])

			return (
				<InternalPopover
					onOpenChange={internalOnOpenChange}
					open={internalOpen}
					modal={modal}
				>
					{anchor && anchorContainerEl ? (
						createPortal(
							<InternalPopoverAnchor asChild>{anchor}</InternalPopoverAnchor>,
							anchorContainerEl,
						)
					) : anchor ? (
						<InternalPopoverAnchor asChild>{anchor}</InternalPopoverAnchor>
					) : null}
					{children && triggerTooltip ? (
						<Tooltip label={triggerTooltip}>
							<InternalPopoverTrigger
								asChild
								onClick={(e) => {
									e.stopPropagation()
								}}
								onKeyDown={(e) => {
									e.stopPropagation()
								}}
							>
								{children}
							</InternalPopoverTrigger>
						</Tooltip>
					) : (
						<InternalPopoverTrigger
							asChild
							onClick={(e) => {
								e.stopPropagation()
							}}
							onKeyDown={(e) => {
								e.stopPropagation()
							}}
						>
							{children}
						</InternalPopoverTrigger>
					)}
					{isPortal ? (
						<InternalPopoverPortal container={container}>
							<InternalPopoverContent
								align={align}
								side={side}
								sideOffset={sideOffset}
								style={style}
								collisionPadding={collisionPadding}
								className={cn(className)}
								onOpenAutoFocus={handleOpenAutoFocus}
								onCloseAutoFocus={(e) => {
									e.preventDefault()
									if (onCloseAutoFocus) {
										onCloseAutoFocus(e)
									}
								}}
								ref={ref}
							>
								{renderedContent}
							</InternalPopoverContent>
						</InternalPopoverPortal>
					) : (
						<InternalPopoverContent
							align={align}
							side={side}
							sideOffset={sideOffset}
							style={style}
							collisionPadding={collisionPadding}
							className={cn(className)}
							onOpenAutoFocus={handleOpenAutoFocus}
							onCloseAutoFocus={(e) => {
								e.preventDefault()
								if (onCloseAutoFocus) {
									onCloseAutoFocus(e)
								}
							}}
							ref={ref}
						>
							{renderedContent}
						</InternalPopoverContent>
					)}
				</InternalPopover>
			)
		},
	),
)
